import React, {memo, useState, useCallback, useEffect} from "react";
import {useTranslation} from "react-i18next";
//local
import SubHeading from "components/SubHeading";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import {getFullName} from "helpers/helper";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {QualificationQuestionsModal} from "../components/QualificationQuestionsModal";
import {QualificationMessageModal} from "../components/QualificationMessageModal";
import {Tag} from "components/Tag";
import {IconCog, IconCheckMark, IconPaperPencil} from "components/icons";
import {Info} from "components/Info";
import {color} from "constants/color.consts";

const OurSuppliersQualificationSettings = memo(
  ({settings, organizationName, isOwner, getQualificationQuestions, onUpdate}) => {
    const {t} = useTranslation();

    const [qualificationQuestions, setQualificationQuestions] = useState([]);
    const [isQualificationQuestionsModal, setQualificationQuestionsModal] =
      useState(false);
    const [isQualificationMessageModal, setQualificationMessageModal] = useState(false);

    const isEnabledSupplierQualification = settings?.enableSuppliersQualification;
    const isBuyerOrgMembersToNotify = !!settings?.buyerOrgMembersToNotify?.length;

    const handleGetQualificationQuestions = useCallback(
      () =>
        getQualificationQuestions().then(({data}) =>
          setQualificationQuestions(
            data.suppliersQualificationQuestions?.map((question) => ({
              ...question,
              files: question.files.map((file) => ({...file, ...file.File})),
            })) ?? []
          )
        ),
      [getQualificationQuestions]
    );

    const saveQualificationMessage = (suppliersQualificationMessage) =>
      onUpdate({suppliersQualificationMessage}).then(() =>
        setQualificationMessageModal(false)
      );

    useEffect(() => {
      handleGetQualificationQuestions();
    }, [handleGetQualificationQuestions]);

    return (
      <>
        <div className="supplier-qualification mb-12">
          <SubHeading
            title="SETTINGS_ORGANIZATION_OUR_SUPPLIERS"
            icon={<IconCheckMark />}
            videoLink={{
              label: `VIDEO_LINK_SUPPLIER_QUALIFICATION_LABEL_2`,
              link: `VIDEO_LINK_SUPPLIER_QUALIFICATION_LINK`,
            }}
          />
          <div className="df-row-center mt-4">
            <CustomRadioSwitch
              checked={settings?.enableSuppliersQualification}
              mediumText
              label={t("SETTINGS_ORGANIZATION_OUR_SUPPLIERS_SWITCH_LABEL")}
              small
              informationText="SETTINGS_ORGANIZATION_OUR_SUPPLIERS_SWITCH_TOOLTIP"
              isActive={(checked) =>
                onUpdate(
                  {enableSuppliersQualification: checked},
                  isOwner,
                  "WRONG_PRIVILEGES_ONLY_OWNER"
                )
              }
            />
          </div>
          <div className="df-row-center mt-2">
            <ButtonTransparent
              name="SETTINGS_ORGANIZATION_OUR_SUPPLIERS_QUESTIONS_BUTTON"
              icon={<IconCog />}
              noBorder
              small
              addClass="fz-14 pl-0 __rotate-on-hover"
              onClick={() => setQualificationQuestionsModal(true)}
            />
            <ButtonTransparent
              name="SETTINGS_ORGANIZATION_OUR_SUPPLIERS_MESSAGE_BUTTON"
              icon={<IconPaperPencil color={color.blue} />}
              noBorder
              small
              addClass="fz-14 ml-2"
              onClick={() => setQualificationMessageModal(true)}
            />
          </div>
          {isEnabledSupplierQualification && isBuyerOrgMembersToNotify && (
            <div className="sub-heading mt-4 df-column-start">
              <div className="df-row-center mb-2">
                <h4 className="ml-0 df-row-center mr-2">
                  <span className="mr-2">
                    {t("SETTINGS_ORGANIZATION_OUR_SUPPLIERS_CONTACROVERRIDES_HEADING")}
                  </span>
                  <Info
                    tooltip="SETTINGS_ORGANIZATION_OUR_SUPPLIERS_CONTACROVERRIDES_TOOLTIP"
                    centerText
                  />
                </h4>
              </div>
              <span className="mb-1 fz-12 __italic">
                {t(
                  "SETTINGS_ORGANIZATION_OUR_SUPPLIERS_CONTACROVERRIDES_EXTRA_HEADING_PT_1"
                )}
                <a href={t("MAIL_TO_SUPPORT_LINK")}>{` ${t("MAIL_TO_SUPPORT")} `}</a>
                {t(
                  "SETTINGS_ORGANIZATION_OUR_SUPPLIERS_CONTACROVERRIDES_EXTRA_HEADING_PT_2"
                )}
              </span>
              <div className="members-to-notify webkit-msedge-chrome-nicer-scrollbar vertical-scroll df-column-start">
                {settings.buyerOrgMembersToNotify.map((member) => (
                  <Tag
                    key={member.id}
                    text={`${getFullName(member.firstName, member.lastName)} - ${
                      member.email
                    }`}
                    addClass="mb-1"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        {isQualificationQuestionsModal && (
          <QualificationQuestionsModal
            initialQuestions={qualificationQuestions}
            isOwner={isOwner}
            onClose={({needUpdateQuestions}) => {
              needUpdateQuestions && handleGetQualificationQuestions();
              setQualificationQuestionsModal(false);
            }}
          />
        )}
        {isQualificationMessageModal && (
          <QualificationMessageModal
            initialMessage={settings.suppliersQualificationMessage}
            organizationName={organizationName}
            onSave={saveQualificationMessage}
            onClose={() => setQualificationMessageModal(false)}
          />
        )}
      </>
    );
  }
);

export default OurSuppliersQualificationSettings;
